import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useParams } from 'react-router-dom'
import { useMainData } from 'config/context/mainDataContext'
import { parseStation } from 'utils/station'

/**
 * Normalises GraphQLErrors and NetworkErrors from Apollo Client as well as general errors into a consistent format.
 *
 * Returns an array of error objects, where each error object contains:
 * - errorType: The type of error (GraphQLError, NetworkError, or Error)
 * - errorCode: The error code if available
 * - errorMessage: The error message
 */
function extractErrors(error) {
  const errors = []

  if (error?.graphQLErrors?.length) {
    error.graphQLErrors.forEach(gqlError => {
      errors.push({
        errorType: 'GraphQLError',
        errorCode: gqlError.extensions?.code ?? null,
        errorMessage: gqlError.message ?? error.message ?? null,
      })
    })
  }

  if (error.networkError) {
    errors.push({
      errorType: 'NetworkError',
      errorCode:
        error.networkError.code ??
        error.networkError.statusCode ??
        null,
      errorMessage:
        error.networkError.message ??
        error.message ??
        null,
    })
  }

  if (errors.length === 0) {
    errors.push({
      errorType: error.name ?? 'Error',
      errorCode: error.code ?? error.statusCode ?? null,
      errorMessage: error.message ?? null,
    })
  }

  return errors
}

const getStation = (mainData, params) => {
  const stationParam = params.station
  if(!stationParam) return null

  const station = parseStation(mainData, stationParam)
  if(!station) return null

  return {
    stationId: station.id,
    stationName: station.name,
  }
}

const withEdgeErrorBoundary = (
  Component,
  FallbackComponent,
  widgetName,
) => {
  // Create a wrapper component so that we can use hooks as otherwise we can't use hooks in HOCs
  function WithEdgeErrorBoundaryWrapper(props) {
    const data = useMainData()
    const params = useParams()

    return (
      <ErrorBoundary
        FallbackComponent={FallbackComponent}
        onError={(error, errorInfo) => {
          const errorContext = {
            id: crypto.randomUUID() + '-' + Date.now(),
            timestamp: Date.now(),
            userId: data?.me?.id,
            companyId: data?.me?.company?.id,
            user: {
              id: data?.me?.id,
              name: [data?.me?.firstName, data?.me?.lastName].filter(Boolean).join(' ') || null,
              email: data?.me?.email,
            },
            company: {
              id: data?.me?.company?.id,
              name: data?.me?.company?.name,
            },
            station: getStation(data, params),
            widget: widgetName || null,
            rawErrorMessage: errorInfo,
          }
          
          const errors = extractErrors(error)

          const transformedErrors = errors.map(error => ({
            ...errorContext,
            errorType: error.errorType,
            errorCode: error.errorCode,
            errorMessage: error.errorMessage,
          }))

          fetch(`${process.env.REACT_APP_GATEWAY_URI}/errors`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(transformedErrors),
          })
        }}
      >
        <Component {...props} widgetName={widgetName} />
      </ErrorBoundary>
    )
  }

  return WithEdgeErrorBoundaryWrapper
}

export { withEdgeErrorBoundary }
